import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Edit, Trash2, Package, ChevronLeft, ChevronRight, BarChart3 } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { sortSizes } from '../../utils/constants';

const ProductCard = ({ product }) => {
  const { collections, deleteProduct } = useApp();

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${product.name}"?`)) {
      deleteProduct(product.id);
    }
  };
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Get the collection name for this product
  const collection = collections.find(c => c.id === product.collectionId || c.id === product.collection_id);
  
  // Handle both old and new product data structure
  const images = product.images || [product.image];
  const totalStock = product.totalStock || product.total_stock || product.stock || 0;
  
  const getStockStatus = (stock) => {
    if (stock === 0) return { text: 'Out of Stock', color: 'bg-red-100 text-red-800' };
    if (stock < 10) return { text: 'Low Stock', color: 'bg-yellow-100 text-yellow-800' };
    return { text: 'In Stock', color: 'bg-green-100 text-green-800' };
  };

  const stockStatus = getStockStatus(totalStock);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden group">
      {/* Image Carousel */}
      <div className="relative">
        <img 
          src={images[currentImageIndex]} 
          alt={product.name}
          className="w-full h-48 object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200" />
        
        {/* Image Navigation */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 p-1 bg-white bg-opacity-80 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-opacity-100"
            >
              <ChevronLeft size={16} className="text-gray-600" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 bg-white bg-opacity-80 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-opacity-100"
            >
              <ChevronRight size={16} className="text-gray-600" />
            </button>
            
            {/* Image Indicators */}
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                  }`}
                />
              ))}
            </div>
          </>
        )}
        
        {/* Stock Status Badge */}
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${stockStatus.color}`}>
            {stockStatus.text}
          </span>
        </div>
        
        {/* Action buttons */}
        <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Link
            to={`/analytics/product/${product.id}`}
            className="p-2 bg-white rounded-lg shadow-sm hover:bg-indigo-50 transition-colors"
            title="View Analytics"
          >
            <BarChart3 size={16} className="text-indigo-600" />
          </Link>
          <Link
            to={`/edit-product/${product.id}`}
            className="p-2 bg-white rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
            title="Edit Product"
          >
            <Edit size={16} className="text-gray-600" />
          </Link>
          <button
            onClick={handleDelete}
            className="p-2 bg-white rounded-lg shadow-sm hover:bg-red-50 transition-colors"
            title="Delete Product"
          >
            <Trash2 size={16} className="text-red-600" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Collection Badge */}
        {collection && (
          <div className="mb-2">
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
              <Package size={12} className="mr-1" />
              {collection.name}
            </span>
          </div>
        )}

        {/* Product Name and Price */}
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {product.name}
          </h3>
          <p className="text-xl font-bold text-indigo-600">
            ${product.price}
          </p>
        </div>

        {/* Sizes */}
        <div className="mb-3">
          <p className="text-sm text-gray-600 mb-1">Sizes:</p>
          <div className="flex flex-wrap gap-1">
            {sortSizes(product.sizes).map((size) => (
              <span 
                key={size}
                className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
              >
                {size}
              </span>
            ))}
          </div>
        </div>

        {/* Colors */}
        <div className="mb-3">
          <p className="text-sm text-gray-600 mb-1">Colors:</p>
          <div className="flex flex-wrap gap-1">
            {product.colors.map((color) => (
              <span 
                key={color}
                className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
              >
                {color}
              </span>
            ))}
          </div>
        </div>

        {/* Stock Info */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">
              Total Stock: {totalStock} units
            </span>
            <Link
              to={`/edit-product/${product.id}`}
              className="text-indigo-600 hover:text-indigo-700 font-medium"
            >
              Edit
            </Link>
          </div>
          
          {/* Size-wise Stock */}
          {(product.sizeStock || product.size_stock) && (
            <div className="text-xs">
              <p className="text-gray-600 mb-1">Stock by size:</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(product.sizeStock || product.size_stock || {}).map(([size, stock]) => (
                  <span 
                    key={size}
                    className={`px-2 py-1 rounded text-xs ${
                      stock === 0 
                        ? 'bg-red-100 text-red-700' 
                        : stock < 5 
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {size}: {stock}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard; 